import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, Gauge } from "lucide-react";
import { API_URL } from "../config";
import { apiRequest, formatApiError } from "../apiRequest";
import KpiPageHeader from "../components/KpiPageHeader";

function currentMonth() {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit" }).formatToParts(new Date());
  const values = Object.fromEntries(parts.map(({ type, value }) => [type, value]));
  return `${values.year}-${values.month}`;
}

async function monthlyRequest(path) {
  const { data } = await apiRequest(`${API_URL}${path}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
  }, { retries: 0 });
  return data;
}

const STATUS_LABELS = {
  calculated: "Calculated",
  missing_data: "Missing data",
  configuration_required: "Configuration required",
  invalid_configuration: "Invalid configuration",
  invalid_input: "Invalid input",
  not_recorded: "Not recorded",
  not_applicable: "Not applicable",
  missing: "No daily row",
};

function statusLabel(status) { return STATUS_LABELS[status] || status || "Unknown"; }
function score(value) { return Number.isFinite(value) ? `${value.toFixed(1)}%` : "—"; }
function MetricStatus({ metric }) {
  return <><span className={`kpi-monthly-status ${metric.status}`}>{statusLabel(metric.status)}</span>
    {metric.sourceStatus && <small className="kpi-monthly-source-status">Source: {statusLabel(metric.sourceStatus)}</small>}</>;
}

export default function KpiMonthlyPage({ onBack, backLabel = "KPI", onHome, onNavigate }) {
  const [month, setMonth] = useState(currentMonth);
  const [team, setTeam] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [directory, setDirectory] = useState({ teams: [], employees: [], restricted: false });
  const [data, setData] = useState({ results: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const isRestricted = useMemo(() => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      return user?.role === "agent" && String(user?.dsrRole || user?.dsr_role || "").toLowerCase() === "tl";
    } catch { return false; }
  }, []);

  const loadDirectory = useCallback(async () => {
    setError("");
    try {
      const params = new URLSearchParams({ month });
      if (team) params.set("team", team);
      const result = await monthlyRequest(`/kpi-monthly/directory?${params}`);
      setDirectory(result);
      if (isRestricted && !team && result.teams?.[0]) setTeam(result.teams[0]);
      if (employeeId && !result.employees?.some((employee) => employee.id === employeeId)) setEmployeeId("");
    } catch (err) { setError(formatApiError(err, "Unable to load KPI workforce")); }
  }, [employeeId, isRestricted, month, team]);

  useEffect(() => { loadDirectory(); }, [loadDirectory]);

  useEffect(() => {
    if (isRestricted && !team) return;
    let active = true;
    const params = new URLSearchParams({ month });
    if (team) params.set("team", team);
    if (employeeId) params.set("employeeId", employeeId);
    setLoading(true);
    monthlyRequest(`/kpi-monthly/results?${params}`)
      .then((result) => { if (active) setData(result); })
      .catch((err) => { if (active) setError(formatApiError(err, "Unable to calculate monthly performance")); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [employeeId, isRestricted, month, team]);

  const selectedEmployee = directory.employees.find((employee) => employee.id === employeeId);

  return <main className="kpi-module-page kpi-monthly-page">
    <KpiPageHeader title="KPI Monthly Performance" subtitle={isRestricted ? "Team performance & individual results" : "Monthly performance & individual results"} icon={CalendarDays} onBack={onHome || onBack} backLabel={onHome ? "Home" : backLabel} activeView="monthly" onNavigate={onNavigate} />
    {error && <div className="kpi-config-message error" role="alert">{error}</div>}
    <section className="kpi-monthly-controls" aria-label="Monthly performance filters">
      <label className="kpi-config-field"><span>Month</span><input type="month" value={month} onChange={(event) => { setMonth(event.target.value); setEmployeeId(""); }} /></label>
      <label className="kpi-config-field"><span>Team</span><select value={team} disabled={isRestricted} onChange={(event) => { setTeam(event.target.value); setEmployeeId(""); }}>
        {!isRestricted && <option value="">All teams</option>}{directory.teams.map((item) => <option key={item} value={item}>{item}</option>)}
      </select></label>
      <label className="kpi-config-field"><span>Employee</span><select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}>
        <option value="">Team monthly view</option>{directory.employees.map((employee) => <option key={employee.id} value={employee.id}>{employee.name}{employee.disabled ? " · Inactive" : ""}</option>)}
      </select></label>
    </section>
    {loading && <p className="kpi-config-loading" role="status">Calculating monthly KPI…</p>}
    {!loading && !employeeId && <section className="kpi-monthly-team" aria-label="Team monthly results">
      <div className="kpi-monthly-section-heading"><h2>{team || "All teams"}</h2><span>{month} · {data.results.length} employee{data.results.length === 1 ? "" : "s"}</span></div>
      <div className="kpi-config-table-wrap"><table className="kpi-config-table kpi-monthly-table"><thead><tr><th>Employee</th><th>Designation</th><th>Team</th><th>Monthly KPI</th><th>Band</th><th>Status</th><th>Values</th></tr></thead><tbody>
        {data.results.map((result) => <tr key={result.employeeId}><th>{result.employeeName}{result.disabled && <small>Inactive</small>}</th><td>{result.designation || "—"}</td><td>{result.team || "—"}</td><td>{score(result.totalScore)}</td><td>{result.performanceBand?.label || (result.performanceBandStatus === "unclassified" ? "Unclassified" : "Not configured")}</td><td><span className={`kpi-monthly-status ${result.status}`}>{statusLabel(result.status)}</span></td><td>{result.recordedValueCount || 0} recorded</td></tr>)}
        {!data.results.length && <tr><td colSpan="6" className="kpi-monthly-empty">No matching employees or KPI data for this month.</td></tr>}
      </tbody></table></div>
    </section>}
    {!loading && employeeId && data.results[0] && <MonthlyDetail result={data.results[0]} employee={selectedEmployee} month={month} />}
    <p className="kpi-monthly-readonly"><Gauge size={15} aria-hidden="true" /> Calculated from daily KPI records and their pinned configuration versions. Viewing this page does not save a monthly score.</p>
  </main>;
}

function MonthlyDetail({ result, employee, month }) {
  return <section className="kpi-monthly-detail" aria-label="Employee monthly result">
    <div className="kpi-monthly-summary">
      <div><span>{employee?.designation || result.designation} · {month}</span><h2>{result.employeeName}</h2><small>{employee?.team || result.team || "Team not assigned"}{result.disabled ? " · Inactive user, historical KPI retained" : ""}</small></div>
      <div><span>Overall KPI</span><strong>{score(result.totalScore)}</strong><span className={`kpi-monthly-status ${result.status}`}>{statusLabel(result.status)}</span></div>
    </div>
    {result.error && <div className="kpi-monthly-warning" role="status">{result.error}</div>}
    {!result.segments?.length && <div className="kpi-monthly-empty">There are no published configuration segments or daily values to calculate.</div>}
    {result.segments?.map((segment) => <section className="kpi-monthly-segment" key={`${segment.configVersionId}-${segment.team}-${segment.designation}`}>
      <header><h3>{segment.designation} · {segment.team || "No team snapshot"}</h3><span>Configuration v{segment.versionNumber ?? "?"}</span><strong>{score(segment.totalScore)}</strong></header>
      {segment.error && <p className="kpi-monthly-warning" role="status">{segment.error}</p>}
      <div className="kpi-config-table-wrap"><table className="kpi-config-table kpi-monthly-table"><thead><tr><th>KPI</th><th>Aggregation</th><th>Weight</th><th>Performance</th><th>Contribution</th><th>Status</th><th>Values</th></tr></thead><tbody>
        {segment.metrics.map((metric) => <tr key={metric.metricKey}><th>{metric.name}{metric.explanation && <small>{metric.explanation}</small>}</th><td>{metric.aggregationMethod || "—"}</td><td>{metric.weightPercent == null ? "—" : `${metric.weightPercent}%`}</td><td>{score(metric.performance)}</td><td>{Number.isFinite(metric.weightedContribution) ? `${metric.weightedContribution.toFixed(1)}%` : "—"}</td><td><MetricStatus metric={metric} /></td><td>{metric.recordedValues} recorded</td></tr>)}
      </tbody></table></div>
    </section>)}
    <p className="kpi-monthly-count">{result.recordedValueCount || 0} recorded daily values in selected month. No calendar-day expectation is inferred.</p>
  </section>;
}
